import styled, { css } from "styled-components";
import { controlSize } from "../../../design-system/tokens/controlSizes";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { rawColors } from "../../../design-system/tokens/colors";
import { borderRadius } from "../../../design-system/tokens/borders";
import { shadows } from "../../../design-system/tokens/shadows";
import {
  authBorderWidth,
  authBrandColors,
  authInk,
  authMotion,
  authRadius,
  authShadows,
  authSizes,
  authTypography,
} from "../../../styles/authBrandTokens";
import type { FormFieldSurface } from "./FormField.types";
import { duration, easing } from "../../../design-system/tokens/transitions";

type SurfaceProps = { $surface: FormFieldSurface };
const isAuth = ({ $surface }: SurfaceProps) => $surface === "auth";

/* Auth: label sits 6px above the input (spec .mb-field gap). */
export const FieldRoot = styled.div<SurfaceProps>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (isAuth(p) ? spacing[1.5] : spacing[2])};
  min-width: 0;
  flex: 1 1 0;
  box-sizing: border-box;
`;

/* inline-flex + gap so a labelIcon (e.g. the AI sparkle on a Goal field)
   sits on the label baseline row — Figma 5326:88486, 4px gap. */
export const FieldLabel = styled.label<SurfaceProps>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  ${(p) => (isAuth(p) ? authTypography("label") : typographyMixin("caption", 3))}
  color: ${(p) => (isAuth(p) ? authInk[900] : rawColors.text.mediumEmphasis)};
`;

/** Label on the left, `labelAction` (e.g. "Forgot password?") on the right. */
export const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing[2]};
  min-width: 0;
`;

export const RequiredMark = styled.span`
  color: ${rawColors.accent.red500};
  margin-left: 2px;
`;

/** Wraps the input when an end adornment overlays the right edge. */
export const InputWithAdornmentWrap = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
`;

export const EndAdornmentSlot = styled.div<SurfaceProps>`
  position: absolute;
  right: ${(p) => (isAuth(p) ? spacing[3] : spacing[2])};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => (isAuth(p) ? authInk[500] : rawColors.text.mediumEmphasis)};
  z-index: 1;

  &:hover {
    color: ${(p) => (isAuth(p) ? authInk[900] : rawColors.text.highEmphasis)};
  }
`;

/**
 * Form-config input — matches the Figma config-form input style:
 *   bg: greys/100 = #FAFAFC (Figma token; not yet in design-system colors,
 *       so used as a literal — when added there it should map to this exact
 *       hex)
 *   border: stroke/200 (#E6E1F3), 1px
 *   radius: 8px (borderRadius.lg)
 *   height: controlSize.configForm.controlHeight (Figma 44px)
 *   typography: body/3 (Plus Jakarta Sans Medium 14/24)
 *
 * We bypass the Input atom here because its `filled` variant pulls from a
 * theme surface color (lavender-tinted on this app) rather than the flat
 * #FAFAFC the design system uses for config forms. Keeping the override at
 * the molecule level so connector wizards stay pixel-matched without forcing
 * a theme-wide change.
 */
const FORM_INPUT_FILL = "#FAFAFC";

type InputStateProps = { $invalid?: boolean; $hasEndAdornment?: boolean };

const authBorder = ({ $invalid }: InputStateProps, resting: string) =>
  $invalid ? authBrandColors.error : resting;

/**
 * Auth spec §4 "Text input". Appended after the base rules so it overrides
 * them at equal specificity; an invalid field keeps its red border and ring
 * even while focused (as in the prototype).
 */
const authInput = css<InputStateProps>`
  height: ${authSizes.controlHeight};
  padding: 0 ${spacing[4]};
  padding-right: ${({ $hasEndAdornment }) =>
    $hasEndAdornment ? spacing[12] : spacing[4]};
  background-color: ${authBrandColors.white};
  border: ${authBorderWidth} solid ${(p) => authBorder(p, authInk[200])};
  border-radius: ${authRadius.input};
  box-shadow: ${({ $invalid }) => ($invalid ? authShadows.errorRing : "none")};
  color: ${authInk[900]};
  ${authTypography("input")}
  transition:
    border-color ${authMotion.control} ${authMotion.easeOut},
    box-shadow ${authMotion.control} ${authMotion.easeOut};

  &::placeholder {
    color: ${authInk[400]};
    font-weight: 400;
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: ${(p) => authBorder(p, authInk[300])};
  }

  &:focus {
    border-color: ${(p) => authBorder(p, rawColors.primary[500])};
    box-shadow: ${({ $invalid }) =>
      $invalid ? authShadows.errorRing : authShadows.focusRing};
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover {
    -webkit-box-shadow: 0 0 0 1000px ${authBrandColors.white} inset;
    -webkit-text-fill-color: ${authInk[900]};
  }

  &:-webkit-autofill:focus {
    -webkit-box-shadow:
      0 0 0 1000px ${authBrandColors.white} inset,
      ${authShadows.focusRing};
    -webkit-text-fill-color: ${authInk[900]};
  }
`;

export const StyledInput = styled.input<{
  $invalid?: boolean;
  $hasEndAdornment?: boolean;
  $surface: FormFieldSurface;
}>`
  width: 100%;
  margin: 0;
  box-sizing: border-box;

  /* config is a fixed-height row; plain is content-sized from its padding
     (Figma _input-small: 10px/12px around a 24px line-box). */
  height: ${({ $surface }) =>
    $surface === "plain" ? "auto" : controlSize.configForm.controlHeight};
  padding: ${({ $surface }) =>
    $surface === "plain" ? `${spacing[2.5]} ${spacing[3]}` : `0 ${spacing[3]}`};
  padding-right: ${({ $hasEndAdornment }) =>
    $hasEndAdornment ? spacing[10] : spacing[3]};

  background-color: ${({ $surface }) =>
    $surface === "plain" ? rawColors.structural.white : FORM_INPUT_FILL};
  box-shadow: ${({ $surface }) =>
    $surface === "plain" ? shadows.elevation[1] : "none"};

  border: 1px solid
    ${({ $invalid }) =>
      $invalid ? rawColors.accent.red500 : rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  color: ${rawColors.text.highEmphasis};
  ${typographyMixin("body", 3)}
  outline: none;
  transition: border-color ${duration.fast} ${easing.smooth};

  &::placeholder {
    color: ${rawColors.text.lowEmphasis};
    /* Figma draws the config-form hint at Medium and the _input-small hint at
       Regular; body/3 is Medium, so only plain steps down. */
    font-weight: ${({ $surface }) => ($surface === "plain" ? 400 : 500)};
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: ${({ $invalid }) =>
      $invalid ? rawColors.accent.red500 : rawColors.stroke[300]};
  }

  &:focus {
    border-color: ${({ $invalid }) =>
      $invalid ? rawColors.accent.red500 : rawColors.primary[500]};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Defeat browser autofill's yellow background — without this, Chrome paints
     a solid yellow over our flat fill until the user clicks elsewhere. */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px
      ${({ $surface }) =>
        $surface === "plain" ? rawColors.structural.white : FORM_INPUT_FILL}
      inset;
    -webkit-text-fill-color: ${rawColors.text.highEmphasis};
    transition: background-color 5000s ease-in-out 0s;
  }

  ${(p) => isAuth(p) && authInput}
`;

/* Auth: 8px below the input (6px gap + 2px), 13/18 medium, icon first. */
const authHelperText = css<{ $error?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[1.5]};
  margin-top: ${spacing[0.5]};
  ${({ $error }) => authTypography($error ? "error" : "small")}
  color: ${({ $error }) => ($error ? authBrandColors.error : authInk[600])};

  /* 14px icon on an 18px line: 2px down keeps it on the first line when the
     message wraps (e.g. the SSO stub error). */
  svg {
    flex: none;
    margin-top: ${spacing[0.5]};
  }
`;

export const HelperText = styled.p<{ $error?: boolean } & SurfaceProps>`
  margin: 0;
  ${typographyMixin("caption", 4)}
  color: ${({ $error }) =>
    $error ? rawColors.accent.red500 : rawColors.text.mediumEmphasis};

  ${(p) => isAuth(p) && authHelperText}
`;
